/*
 * Discount code panel of the cart drawer (sections/cart-drawer.liquid), replacing the order note: the same slide-up
 * <dialog-element> the note used, opened by the "Add discount code" button in the footer.
 *
 * Codes are applied through the Ajax cart (/cart/update.js with "discount"), so they are carried to checkout. Applying
 * a code replaces the current one; a code the cart can't use is rolled back and reported. The applied codes come from
 * cart.js (discount_codes) and are kept in sync with the theme's cart:change events, since the drawer footer holding
 * this element is re-rendered on every cart change.
 */
(() => {
  if (window.customElements.get("cart-discount-code")) {
    return;
  }

  const routesRoot = () => window.Shopify?.routes?.root || "/";
  const instances = new Set();

  let codes = null;
  let error = { message: "", at: 0 };

  // Translated labels, read from the element's data-label-* attributes (locales/*.json "cart_discount")
  const labels = {
    add: "Add discount code",
    change: "Change discount code",
    invalid: "“__code__” can't be applied to the items in your cart.",
    error: "Something went wrong, please try again.",
    remove: "Remove discount code __code__"
  };

  const setCodes = (discountCodes) => {
    codes = discountCodes || [];
    instances.forEach((instance) => instance.render());
  };

  const loadCodes = async () => {
    const cart = await (await fetch(`${routesRoot()}cart.js`)).json();
    setCodes(cart.discount_codes);
  };

  const updateCodes = async (list) => {
    const response = await fetch(`${routesRoot()}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ discount: list.join(","), sections: "cart-drawer" })
    });
    const cart = await response.json();

    if (!response.ok) {
      throw new Error(cart.description || cart.message || labels.error);
    }

    return cart;
  };

  document.addEventListener("cart:change", (event) => {
    if (event.detail?.cart?.discount_codes) {
      setCodes(event.detail.cart.discount_codes);
    }
  });

  document.addEventListener("cart:refresh", loadCodes);

  class CartDiscountCode extends HTMLElement {
    connectedCallback() {
      instances.add(this);

      Object.keys(labels).forEach((key) => {
        const value = this.dataset[`label${key[0].toUpperCase()}${key.slice(1)}`];
        if (value) {
          labels[key] = value;
        }
      });

      this.form = this.querySelector("form");
      this.input = this.querySelector('input[name="discount"]');

      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.apply(this.input.value.trim());
      });

      this.addEventListener("click", (event) => {
        const removeButton = event.target.closest("[data-remove-code]");
        if (removeButton) {
          this.save(this.applied.filter((code) => code !== removeButton.dataset.removeCode));
        }
      });

      if (codes === null) {
        loadCodes();
      } else {
        this.render();
      }
    }

    disconnectedCallback() {
      instances.delete(this);
    }

    get applied() {
      return (codes || []).filter((code) => code.applicable).map((code) => code.code);
    }

    render() {
      const applied = this.applied;
      const list = this.querySelector("[data-codes]");

      list.replaceChildren(...applied.map((code) => {
        const item = document.createElement("li");
        item.className = "cart-discount__chip";
        item.innerHTML = `<svg role="presentation" focusable="false" width="14" height="13" viewBox="0 0 14 13" fill="none"><path d="M7.25 11.383a1.74 1.74 0 0 0 2.195.105c.478-.35.957-.722 1.39-1.155.433-.433.804-.912 1.154-1.39.486-.662.45-1.588-.104-2.195a65.945 65.945 0 0 0-4.97-4.896 1.588 1.588 0 0 0-.75-.377c-1.017-.197-3.552-.613-4.177.012-.625.624-.208 3.16-.011 4.176.055.28.186.537.376.75a65.976 65.976 0 0 0 4.896 4.97Z" stroke="currentColor" stroke-width="1.5"/><path d="M5.729 5.227a1.036 1.036 0 1 0-1.466-1.466 1.036 1.036 0 0 0 1.466 1.466Z" fill="currentColor"/></svg><span></span><button type="button" aria-label="">&times;</button>`;
        item.querySelector("span").textContent = code;
        item.querySelector("button").dataset.removeCode = code;
        item.querySelector("button").setAttribute("aria-label", labels.remove.replace("__code__", code));
        return item;
      }));
      list.hidden = applied.length === 0;

      // The toggle button sits next to the panel, like the theme's "Add order note"
      const label = this.closest('[slot="footer"]')?.querySelector("[data-discount-label]");
      if (label) {
        label.textContent = applied.length > 0 ? `${labels.change} (${applied.join(", ")})` : labels.add;
      }

      // The footer is re-rendered right after a failed attempt, so a fresh error is shown again on the new panel
      const errorElement = this.querySelector("[data-error]");
      const showError = error.message && Date.now() - error.at < 8000;
      errorElement.hidden = !showError;
      errorElement.textContent = showError ? error.message : "";

      if (showError) {
        this.closest("dialog-element")?.show();
      }
    }

    async apply(code) {
      if (!code) {
        this.input.focus();
        return;
      }

      const previous = this.applied;
      const cart = await this.save([code], async (updatedCart) => {
        const result = updatedCart.discount_codes?.find((entry) => entry.code.toLowerCase() === code.toLowerCase());

        if (result?.applicable) {
          return updatedCart;
        }

        error = { message: labels.invalid.replace("__code__", code), at: Date.now() };
        return updateCodes(previous);
      });

      if (cart && error.message && Date.now() - error.at < 8000) {
        this._findCurrent()?.querySelector('input[name="discount"]')?.setAttribute("value", code);
      }
    }

    async save(list, check = null) {
      if (this.busy) {
        return null;
      }

      this.busy = true;
      this.toggleAttribute("aria-busy", true);
      error = { message: "", at: 0 };

      try {
        let cart = await updateCodes(list);

        if (check) {
          cart = await check(cart);
        }

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: { baseEvent: "variant:add", cart }
        }));

        return cart;
      } catch (exception) {
        error = { message: exception.message, at: Date.now() };
        this.render();
        return null;
      } finally {
        this.busy = false;
        this.removeAttribute("aria-busy");
      }
    }

    // The element shown after the drawer footer has been re-rendered
    _findCurrent() {
      return [...instances].find((instance) => instance.isConnected);
    }
  }

  window.customElements.define("cart-discount-code", CartDiscountCode);
})();
